import { useQuery } from '@apollo/client'
import { Autocomplete, TextField, Chip } from '@mui/material'
import {
    CandidateFilterCategory,
    SearchIndexField,
} from '__generated__/graphql'
import { useMemo } from 'react'
import { FilterWrapper } from 'recruiter/components/FilterWrapper'
import { useRosterSearchFilter } from 'recruiter/hooks'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'

interface ProcessStatusOption {
    id: string
    label: string
    groupBy: string
}

export function RosterCandidateProcessStatusFilter() {
    const { data, loading } = useQuery(GetProcessStatuses)
    const { value, setValue } = useRosterSearchFilter(
        SearchIndexField.RosterCandidateProcessStatus
    )

    const allProcessStatusOptions = useMemo(
        () =>
            data?.processStatuses
                ?.map((ps) => ({
                    id: ps.id,
                    label: ps.statusLabel,
                    groupBy: ps.processStatusSummaryGroup?.label ?? 'No Group',
                }))
                .sort((a, b) => a.groupBy.localeCompare(b.groupBy)) ?? [],
        [data?.processStatuses]
    )

    const selectedProcessStatuses = useMemo(() => {
        const processStatusIdsInFilter = value?.value?.stringArray ?? []
        return allProcessStatusOptions.filter((option) =>
            processStatusIdsInFilter.includes(option.id)
        )
    }, [allProcessStatusOptions, value])

    const handleChange = (_: unknown, newValue: ProcessStatusOption[]) => {
        if (newValue.length === 0) {
            setValue(null)
            return
        }

        setValue({
            category: CandidateFilterCategory.All,
            value: { stringArray: newValue.map((v) => v.id) },
        })
    }

    return (
        <FilterWrapper label="Process Status">
            <Autocomplete
                multiple
                options={allProcessStatusOptions}
                loading={loading}
                value={selectedProcessStatuses}
                onChange={handleChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                groupBy={(option) => option.groupBy}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search Process Statuses..."
                    />
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                        />
                    ))
                }
            />
        </FilterWrapper>
    )
}
